/* ============================================================
   Bouquet Builder — Dumbo Balloons
   Guided wizard. Vanilla JS, self-contained per section root.

   Steps (configurable): 1 Weight → 2..N typed balloon steps
   (Latex / Foil accents / Numbers & hero foils) → final Describe.
   Each balloon step has a LEFT-HAND category rail (its collections),
   a product grid (with per-card quantity), and a shared "This bouquet"
   tray. Repeatable: build multiple bouquets, then checkout via /cart.

   Data is server-rendered into JSON islands (no fragile AJAX):
     [data-bb-weights]  -> weight options
     [data-bb-products] -> { collectionHandle: [products] }
     [data-bb-config]   -> { steps:[...], collectionTitles:{...} }

   Cart grouping: every line of a bouquet carries
     _bouquet_id        (underscore -> hidden, groups on the order)
     Bouquet            (visible label, e.g. "Bouquet 1")
     Bouquet description(visible customer note)
   Internal money unit = CENTS (integers) to avoid float drift.
   ============================================================ */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  ready(function () {
    document.querySelectorAll('[data-bouquet-builder]').forEach(initBuilder);
  });

  function initBuilder(root) {
    var cfg = { currency: root.dataset.currency || 'USD' };

    // ---- server-rendered islands (read BEFORE we overwrite innerHTML) ----
    var rawWeights = parseIsland(root.querySelector('[data-bb-weights]')) || [];
    var rawProducts = parseIsland(root.querySelector('[data-bb-products]')) || {};
    var config = parseIsland(root.querySelector('[data-bb-config]')) || { steps: [], collectionTitles: {} };

    // normalize the per-collection product map once
    var byColl = {};
    Object.keys(rawProducts).forEach(function (h) {
      byColl[h] = (rawProducts[h] || []).map(normProduct).filter(Boolean);
    });

    var state = {
      step: 1,
      weights: rawWeights.map(function (w) {
        return { variantId: w.variantId, title: w.title, priceCents: w.priceCents, image: fixUrl(w.image) };
      }),
      steps: buildSteps(),                    // balloon steps (n starts at 2)
      activeCat: {},                          // stepKey -> collection handle | '__all__'
      search: {},                             // stepKey -> string
      current: { weight: null, items: [], description: '' },
      committed: []
    };
    var BALLOON_COUNT = state.steps.length;
    var DESCRIBE_STEP = 2 + BALLOON_COUNT;    // last step

    // ---- DOM scaffold -------------------------------------------------
    root.innerHTML = template();
    var els = {
      progress: root.querySelector('[data-bb=progress]'),
      strip: root.querySelector('[data-bb=strip]'),
      panelWeight: root.querySelector('[data-bb=panel-weight]'),
      panelBrowse: root.querySelector('[data-bb=panel-browse]'),
      panelDescribe: root.querySelector('[data-bb=panel-describe]'),
      weights: root.querySelector('[data-bb=weights]'),
      browseTitle: root.querySelector('[data-bb=browse-title]'),
      rail: root.querySelector('[data-bb=rail]'),
      search: root.querySelector('[data-bb=search]'),
      grid: root.querySelector('[data-bb=grid]'),
      tray: root.querySelector('[data-bb=tray]'),
      describe: root.querySelector('[data-bb=describe]'),
      preview: root.querySelector('[data-bb=preview]'),
      nav: root.querySelector('[data-bb=nav]'),
      navBrowse: root.querySelector('[data-bb=nav-browse]'),
      bar: root.querySelector('[data-bb=bar]'),
      toast: root.querySelector('[data-bb=toast]'),
      toTop: root.querySelector('[data-bb=to-top]')
    };

    // ---- Scroll-to-top floating button -------------------------------
    if (els.toTop) {
      els.toTop.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
      var toTopTicking = false;
      window.addEventListener('scroll', function () {
        if (toTopTicking) return;
        toTopTicking = true;
        requestAnimationFrame(function () {
          els.toTop.classList.toggle('is-visible', window.scrollY > 300);
          toTopTicking = false;
        });
      }, { passive: true });
    }

    els.search.addEventListener('input', function () {
      var st = currentBalloonStep();
      if (st) { state.search[st.key] = els.search.value; renderGrid(st); }
    });

    // ---- bootstrap ----------------------------------------------------
    renderWeights();
    showStep(1);

    // ====================================================================
    // Step building from config
    // ====================================================================
    function buildSteps() {
      // flatten unique products with the collections they belong to
      var flat = {}; // id -> { p, colls:[handles] }
      Object.keys(byColl).forEach(function (h) {
        (byColl[h] || []).forEach(function (p) {
          if (!flat[p.id]) flat[p.id] = { p: p, colls: [] };
          flat[p.id].colls.push(h);
        });
      });
      var ids = Object.keys(flat);
      var titles = config.collectionTitles || {};
      var steps = config.steps || [];

      function hasTag(p, t) {
        if (!t) return false;
        return (p.tags || []).indexOf(String(t).toLowerCase()) !== -1;
      }
      function inStep(p, cs) {
        if (cs.tag) return hasTag(p, cs.tag);
        if (cs.rest) {
          // "the rest" = not claimed by any tag-defined step
          return !steps.some(function (s) { return s.tag && hasTag(p, s.tag); });
        }
        return false;
      }

      return steps.map(function (cs, idx) {
        var prods = [];
        var catMap = {};
        ids.forEach(function (id) {
          var rec = flat[id];
          if (!inStep(rec.p, cs)) return;
          prods.push(rec.p);
          rec.colls.forEach(function (h) { (catMap[h] = catMap[h] || []).push(rec.p); });
        });
        var cats = Object.keys(catMap).map(function (h) {
          return { handle: h, title: titles[h] || h, products: catMap[h] };
        }).filter(function (c) { return c.products.length; })
          .sort(function (a, b) { return a.title.localeCompare(b.title); });
        return { n: idx + 2, key: cs.key || ('step' + (idx + 2)), title: cs.title || ('Step ' + (idx + 2)), products: prods, cats: cats };
      });
    }

    // ====================================================================
    // Money + helpers (hoisted)
    // ====================================================================
    var _nf;
    function fmt(cents) {
      if (_nf === undefined) {
        try { _nf = new Intl.NumberFormat(undefined, { style: 'currency', currency: cfg.currency }); }
        catch (e) { _nf = null; }
      }
      return _nf ? _nf.format((cents || 0) / 100) : '$' + ((cents || 0) / 100).toFixed(2);
    }
    function fixUrl(u) { return !u ? '' : (u.indexOf('//') === 0 ? 'https:' + u : u); }
    function normProduct(p) {
      var variants = (p.variants || []).map(function (v) {
        return { id: v.id, title: v.title, priceCents: v.priceCents, available: v.available !== false };
      });
      if (!variants.length) return null;
      var v0 = variants.filter(function (v) { return v.available; })[0] || variants[0];
      return {
        id: p.id, handle: p.handle, title: p.title, image: fixUrl(p.image),
        cutout: p.cutout ? fixUrl(p.cutout) : '',
        tags: (p.tags || []).map(function (t) { return String(t).toLowerCase(); }),
        variants: variants, priceCents: v0.priceCents,
        soldOut: !variants.some(function (v) { return v.available; })
      };
    }
    function parseIsland(el) { if (!el) return null; try { return JSON.parse(el.textContent); } catch (e) { return null; } }
    function clone(o) { return JSON.parse(JSON.stringify(o)); }
    function esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function isBalloonStep(n) { return n >= 2 && n <= 1 + BALLOON_COUNT; }
    function balloonStepByNum(n) { return state.steps[n - 2]; }
    function currentBalloonStep() { return isBalloonStep(state.step) ? balloonStepByNum(state.step) : null; }

    // ====================================================================
    // Step display
    // ====================================================================
    function showStep(n) {
      state.step = n;
      els.panelWeight.classList.toggle('bb__hidden', n !== 1);
      els.panelBrowse.classList.toggle('bb__hidden', !isBalloonStep(n));
      els.panelDescribe.classList.toggle('bb__hidden', n !== DESCRIBE_STEP);
      renderProgress();
      renderNav();
      if (isBalloonStep(n)) renderBrowse(balloonStepByNum(n));
      if (n === DESCRIBE_STEP) { renderDescribe(); renderPreview(); }
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function renderProgress() {
      var labels = ['Weight'].concat(state.steps.map(function (s) { return s.title; })).concat(['Describe']);
      els.progress.innerHTML = labels.map(function (label, i) {
        var n = i + 1;
        var cls = n === state.step ? 'is-active' : (n < state.step ? 'is-done' : '');
        var sep = i < labels.length - 1 ? '<span class="bb__step-sep"></span>' : '';
        return '<span class="bb__step ' + cls + '"><span class="bb__step-dot">' +
          (n < state.step ? '✓' : n) + '</span><span class="bb__step-label">' + esc(label) + '</span></span>' + sep;
      }).join('');
    }

    function renderWeights() {
      if (!state.weights.length) {
        els.weights.innerHTML = '<div class="bb__empty">Weights are temporarily unavailable.</div>';
        return;
      }
      els.weights.innerHTML = state.weights.map(function (w, i) {
        var sel = state.current.weight && state.current.weight.variantId === w.variantId;
        return '<button type="button" class="bb__weight-card' + (sel ? ' is-selected' : '') + '" data-weight="' + i + '">' +
          (w.image ? '<img src="' + w.image + '" alt="' + esc(w.title) + '" loading="lazy">' : '') +
          '<span class="bb__weight-name">' + esc(w.title) + '</span>' +
          '<span class="bb__weight-price">' + fmt(w.priceCents) + '</span></button>';
      }).join('');
      els.weights.querySelectorAll('[data-weight]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          state.current.weight = state.weights[+btn.dataset.weight];
          renderWeights(); renderNav();
        });
      });
    }

    // ---- balloon browse step (rail + grid + tray) ----------------------
    function renderBrowse(step) {
      els.browseTitle.textContent = step.n + '. ' + step.title;
      els.search.value = state.search[step.key] || '';
      renderRail(step);
      renderGrid(step);
      renderTray();
    }

    function renderRail(step) {
      var active = state.activeCat[step.key] || '__all__';
      var items = [{ handle: '__all__', title: 'All', products: step.products }].concat(step.cats);
      els.rail.innerHTML = items.map(function (c) {
        return '<button type="button" class="bb__rail-item' + (c.handle === active ? ' is-active' : '') +
          '" data-cat="' + esc(c.handle) + '">' + esc(c.title) +
          '<span class="bb__rail-count">' + c.products.length + '</span></button>';
      }).join('');
      els.rail.querySelectorAll('[data-cat]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          state.activeCat[step.key] = btn.dataset.cat;
          renderRail(step); renderGrid(step);
        });
      });
    }

    // Strip a redundant leading size word (e.g. "Small - 6 balloons" -> "6 balloons")
    function cleanVariant(t) {
      return String(t || '').replace(/^\s*(x-?small|xs|small|medium|x-?large|xl|large)\s*[-–—:]\s*/i, '').trim();
    }

    function gridProducts(step) {
      var active = state.activeCat[step.key] || '__all__';
      var list;
      if (active === '__all__') {
        // group by category so like items (e.g. all stars) sit together.
        // Assign each product to its LAST matching category so type collections
        // (Star Balloons, Heart Foil…) win over a generic one (Foil Shapes).
        var catIndex = {};
        step.cats.forEach(function (cat, ci) {
          cat.products.forEach(function (p) { catIndex[p.id] = ci; });
        });
        var groups = step.cats.map(function () { return []; });
        var extra = [];
        step.products.forEach(function (p) {
          if (catIndex[p.id] !== undefined) groups[catIndex[p.id]].push(p);
          else extra.push(p);
        });
        list = [];
        groups.forEach(function (g) { g.forEach(function (p) { list.push(p); }); });
        extra.forEach(function (p) { list.push(p); });
      } else {
        var cat = step.cats.filter(function (c) { return c.handle === active; })[0];
        list = cat ? cat.products : step.products;
      }
      var q = (state.search[step.key] || '').trim().toLowerCase();
      if (q) list = list.filter(function (p) { return p.title.toLowerCase().indexOf(q) !== -1; });
      // sold-out products stay visible (so customers know they exist) but sink to the end
      return list.filter(function (p) { return !p.soldOut; }).concat(list.filter(function (p) { return p.soldOut; }));
    }

    function renderGrid(step) {
      var products = gridProducts(step);
      if (!products.length) {
        els.grid.innerHTML = '<div class="bb__empty">No balloons here' + (state.search[step.key] ? ' for “' + esc(state.search[step.key]) + '”' : '') + '.</div>';
        return;
      }
      els.grid.innerHTML = products.map(function (p, i) {
        var v0 = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
        if (p.soldOut) {
          return '<div class="bb__product is-soldout" data-product="' + i + '">' +
            (p.image ? '<img class="bb__product-media" src="' + p.image + '" alt="' + esc(p.title) + '" loading="lazy">' : '<div class="bb__product-media"></div>') +
            '<div class="bb__product-body">' +
              '<p class="bb__product-title">' + esc(p.title) + '</p>' +
              '<span class="bb__product-price">' + fmt(v0.priceCents) + '</span>' +
              '<button type="button" class="bb__product-add" disabled>Sold out</button>' +
            '</div></div>';
        }
        var variantSel = p.variants.length > 1
          ? '<select class="bb__product-variants" data-variant-select>' + p.variants.map(function (v) {
              return '<option value="' + v.id + '"' + (v.available ? '' : ' disabled') + '>' + esc(cleanVariant(v.title)) + ' — ' + fmt(v.priceCents) + '</option>';
            }).join('') + '</select>'
          : '';
        return '<div class="bb__product" data-product="' + i + '">' +
          (p.image ? '<img class="bb__product-media" src="' + p.image + '" alt="' + esc(p.title) + '" loading="lazy">' : '<div class="bb__product-media"></div>') +
          '<div class="bb__product-body">' +
            '<p class="bb__product-title">' + esc(p.title) + '</p>' +
            '<span class="bb__product-price">' + fmt(v0.priceCents) + '</span>' +
            variantSel +
            '<div class="bb__product-qty">' +
              '<button type="button" class="bb__qstep" data-qstep="-1" aria-label="Decrease quantity">−</button>' +
              '<input type="number" class="bb__qty-input" min="1" value="1" inputmode="numeric" aria-label="Quantity">' +
              '<button type="button" class="bb__qstep" data-qstep="1" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="bb__product-add" data-add="' + i + '">Add to bouquet</button>' +
          '</div></div>';
      }).join('');

      els.grid.querySelectorAll('.bb__product:not(.is-soldout)').forEach(function (card) {
        var input = card.querySelector('.bb__qty-input');
        card.querySelectorAll('[data-qstep]').forEach(function (b) {
          b.addEventListener('click', function () {
            var v = Math.max(1, (parseInt(input.value, 10) || 1) + (+b.dataset.qstep));
            input.value = v;
          });
        });
        input.addEventListener('change', function () {
          input.value = Math.max(1, parseInt(input.value, 10) || 1);
        });
        var addBtn = card.querySelector('[data-add]');
        addBtn.addEventListener('click', function () {
          var p = products[+addBtn.dataset.add];
          var sel = card.querySelector('[data-variant-select]');
          var variantId = sel ? +sel.value : (p.variants.filter(function (v) { return v.available; })[0] || p.variants[0]).id;
          var variant = p.variants.filter(function (v) { return v.id === variantId; })[0];
          var qty = Math.max(1, parseInt(input.value, 10) || 1);
          addBalloon(p, variant, qty);
          input.value = 1;
          addBtn.textContent = '✓ Added';
          addBtn.classList.add('is-added');
          setTimeout(function () { addBtn.textContent = 'Add to bouquet'; addBtn.classList.remove('is-added'); }, 900);
        });
      });
    }

    // ---- shared tray ---------------------------------------------------
    function renderTray() {
      var c = state.current;
      var total = c.weight ? c.weight.priceCents : 0;
      var lines = '';
      if (c.weight) lines += trayRow(c.weight.image, c.weight.title + ' (weight)', null, c.weight.priceCents);
      c.items.forEach(function (it, idx) {
        total += it.priceCents * it.qty;
        var name = it.title + (it.variantTitle && it.variantTitle !== 'Default Title' ? ' · ' + cleanVariant(it.variantTitle) : '');
        lines += trayRow(it.image, name, idx, it.priceCents * it.qty, it.qty);
      });
      els.tray.innerHTML =
        '<div class="bb__tray-head"><strong>This bouquet</strong></div>' +
        (c.weight || c.items.length
          ? '<ul class="bb__tray-list">' + lines + '</ul><div class="bb__tray-total">Subtotal: ' + fmt(total) + '</div>'
          : '<p class="bb__tray-empty">Add balloons and they’ll show up here.</p>');
      els.tray.querySelectorAll('[data-qty]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = +btn.dataset.qty, dir = btn.dataset.dir, it = c.items[idx];
          if (!it) return;
          if (dir === 'inc') it.qty++;
          else if (dir === 'dec') it.qty = Math.max(1, it.qty - 1);
          else if (dir === 'rm') c.items.splice(idx, 1);
          renderTray(); renderNav(); renderBouquetStrip();
        });
      });
    }
    function trayRow(img, name, idx, priceCents, qty) {
      var controls = idx !== null
        ? '<span class="bb__tray-controls"><span class="bb__tray-qty">' +
            '<button type="button" data-qty="' + idx + '" data-dir="dec" aria-label="Decrease">−</button><span>' + qty +
            '</span><button type="button" data-qty="' + idx + '" data-dir="inc" aria-label="Increase">+</button></span>' +
            '<button type="button" class="bb__tray-remove" data-qty="' + idx + '" data-dir="rm" aria-label="Remove">×</button></span>'
        : '';
      return '<li class="bb__tray-item">' +
        '<span class="bb__tray-thumb">' + (img ? '<img src="' + img + '" alt="">' : '') + '</span>' +
        '<span class="bb__tray-item-name">' + esc(name) + '</span>' +
        '<span class="bb__tray-price">' + fmt(priceCents) + '</span>' + controls + '</li>';
    }

    // ---- live tiered "your bouquet" strip (top of builder) ------------
    function renderBouquetStrip() {
      var items = state.current.items;
      var total = items.reduce(function (s, it) { return s + it.qty; }, 0);
      if (!total) { els.strip.classList.add('bb__hidden'); els.strip.innerHTML = ''; return; }
      els.strip.classList.remove('bb__hidden');
      // tiers top -> bottom: hero/numbers, foil accents, latex
      var tiers = [{ key: 'hero_foil', cls: 'hero' }, { key: 'foil_accent', cls: 'accent' }, { key: 'latex', cls: 'latex' }];
      var rows = tiers.map(function (t) {
        var ti = items.filter(function (it) { return it.tier === t.key; });
        if (!ti.length) return '';
        return '<div class="bb__strip-tier bb__strip-' + t.cls + '">' + ti.map(function (it) {
          return '<span class="bb__strip-item">' +
            '<img src="' + esc(it.cutout || it.image) + '" data-fallback="' + esc(it.image) + '" alt="' + esc(it.title) + '">' +
            (it.qty > 1 ? '<span class="bb__strip-badge">×' + it.qty + '</span>' : '') +
            '</span>';
        }).join('') + '</div>';
      }).join('');
      els.strip.innerHTML =
        '<div class="bb__strip-cap">Your bouquet · ' + total + ' balloon' + (total > 1 ? 's' : '') + '</div>' + rows;
      // graceful fallback to the original photo if a cutout asset is missing
      els.strip.querySelectorAll('img[data-fallback]').forEach(function (img) {
        img.addEventListener('error', function handler() {
          img.removeEventListener('error', handler);
          if (img.dataset.fallback && img.getAttribute('src') !== img.dataset.fallback) img.src = img.dataset.fallback;
        });
      });
    }

    // ---- describe + preview -------------------------------------------
    function renderDescribe() {
      els.describe.innerHTML =
        '<textarea id="bb-desc" aria-label="Describe your bouquet" placeholder="Anything we should know about the styling, like table or floor height, organic or structured styling. Tell us here.">' +
        esc(state.current.description || '') + '</textarea>';
      var ta = els.describe.querySelector('#bb-desc');
      ta.addEventListener('input', function () { state.current.description = ta.value; });
    }
    function renderPreview() {
      var c = state.current;
      var total = c.weight ? c.weight.priceCents : 0;
      var items = c.items.map(function (it) {
        total += it.priceCents * it.qty;
        return '<li class="bb__tray-item"><span class="bb__tray-item-name">' + esc(it.title) + '</span><span>× ' + it.qty + '</span></li>';
      }).join('');
      els.preview.innerHTML =
        '<h4>Bouquet ' + (state.committed.length + 1) + ' preview</h4>' +
        '<ul class="bb__tray-list">' +
        '<li class="bb__tray-item"><span class="bb__tray-item-name">⚓ ' + esc(c.weight ? c.weight.title : '') + ' (weight)</span><span>' + fmt(c.weight ? c.weight.priceCents : 0) + '</span></li>' +
        items + '</ul><div class="bb__tray-total">Bouquet total: ' + fmt(total) + '</div>' +
        (c.items.length ? '' : '<p class="bb__describe-hint">Add at least one balloon (go back a step) before adding this bouquet.</p>');
    }

    // ---- nav -----------------------------------------------------------
    function renderNav() {
      var n = state.step, html = '';
      if (n === 1) {
        var t = state.steps.length ? state.steps[0].title : 'balloons';
        html = '<span></span><button type="button" class="bb__btn bb__btn--primary" data-next ' +
          (state.current.weight ? '' : 'disabled') + '>Next: ' + esc(t) + ' →</button>';
      } else if (isBalloonStep(n)) {
        var nextLabel = (n === 1 + BALLOON_COUNT) ? 'Describe' : state.steps[n - 1].title;
        html = '<button type="button" class="bb__btn bb__btn--primary" data-next>Next: ' + esc(nextLabel) + ' →</button>' +
          '<button type="button" class="bb__btn bb__btn--ghost" data-back>← Back</button>';
      } else { // describe
        html = '<button type="button" class="bb__btn bb__btn--ghost" data-back>← Back</button>' +
          '<button type="button" class="bb__btn bb__btn--primary" data-add-bouquet ' +
          (state.current.items.length ? '' : 'disabled') + '>Add to cart</button>';
      }
      els.nav.innerHTML = '';
      if (els.navBrowse) els.navBrowse.innerHTML = '';
      var target = (isBalloonStep(n) && els.navBrowse) ? els.navBrowse : els.nav;
      target.innerHTML = html;
      var next = target.querySelector('[data-next]');
      var back = target.querySelector('[data-back]');
      var addB = target.querySelector('[data-add-bouquet]');
      if (next) next.addEventListener('click', function () { showStep(state.step + 1); });
      if (back) back.addEventListener('click', function () { showStep(state.step - 1); });
      if (addB) addB.addEventListener('click', commitBouquet);
    }

    // ====================================================================
    // Mutations
    // ====================================================================
    function addBalloon(p, variant, qty) {
      if (!variant || !variant.available) return;
      qty = Math.max(1, qty || 1);
      var existing = state.current.items.filter(function (it) { return it.variantId === variant.id; })[0];
      if (existing) existing.qty += qty;
      else state.current.items.push({
        variantId: variant.id, productId: p.id, title: p.title, variantTitle: variant.title,
        priceCents: variant.priceCents, image: p.image, cutout: p.cutout,
        tier: (currentBalloonStep() || {}).key, qty: qty
      });
      renderTray(); renderNav(); renderBouquetStrip();
    }

    function commitBouquet() {
      var c = state.current;
      if (!c.weight || !c.items.length) return;
      var btn = els.nav.querySelector('[data-add-bouquet]');
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      var label = 'Bouquet ' + (state.committed.length + 1);
      var props = {
        '_bouquet_id': 'bq-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
        'Bouquet': label,
        'Bouquet description': (c.description || '').trim() || '(no notes added)'
      };
      var items = [{ id: c.weight.variantId, quantity: 1, properties: clone(props) }];
      c.items.forEach(function (it) { items.push({ id: it.variantId, quantity: it.qty, properties: clone(props) }); });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) {
          return r.text().then(function (t) {
            var msg = 'Add failed (' + r.status + ')';
            try { var j = JSON.parse(t); if (j && j.description) msg = j.description; } catch (e) {}
            throw new Error(msg);
          });
        }
        return r.json();
      }).then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); })
        .then(function (cart) {
          state.committed.push({ label: label });
          toast(label + ' added to cart 🎈');
          state.current = { weight: null, items: [], description: '' };
          renderWeights();
          renderBouquetStrip();
          renderBar(cart);
          showStep(1);
        }).catch(function (err) {
          toast('Sorry — ' + err.message, true);
          if (btn) { btn.disabled = false; btn.textContent = 'Add to cart'; }
        });
    }

    function renderBar(cart) {
      var count = state.committed.length;
      if (!count) { els.bar.classList.add('bb__hidden'); return; }
      els.bar.classList.remove('bb__hidden');
      els.bar.innerHTML =
        '<div class="bb__checkout-bar-info"><strong>' + count + ' bouquet' + (count > 1 ? 's' : '') + ' added</strong>' +
        '<small>Cart subtotal: ' + fmt(cart.total_price) + '</small></div>' +
        '<a class="bb__btn bb__btn--primary" href="/cart">View cart &amp; checkout →</a>';
    }

    // ====================================================================
    // UI utilities
    // ====================================================================
    var toastTimer;
    function toast(msg, isError) {
      els.toast.textContent = msg;
      els.toast.style.background = isError ? '#b3261e' : '';
      els.toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { els.toast.classList.remove('is-visible'); }, 2600);
    }

    // ====================================================================
    // Markup template
    // ====================================================================
    function template() {
      return '' +
        '<div class="bb__progress" data-bb="progress"></div>' +
        '<div class="bb__strip bb__hidden" data-bb="strip" aria-live="polite"></div>' +
        '<section class="bb__panel" data-bb="panel-weight">' +
          '<h2 class="bb__panel-title">1. Choose a balloon weight</h2>' +
          '<div class="bb__weights" data-bb="weights"></div>' +
        '</section>' +
        '<section class="bb__panel bb__hidden" data-bb="panel-browse">' +
          '<h2 class="bb__panel-title" data-bb="browse-title"></h2>' +
          '<div class="bb__browse">' +
            '<nav class="bb__rail" data-bb="rail" aria-label="Categories"></nav>' +
            '<div class="bb__browse-main">' +
              '<input type="search" class="bb__search" data-bb="search" placeholder="Search this step…">' +
              '<div class="bb__grid" data-bb="grid"></div>' +
            '</div>' +
            '<div class="bb__browse-side">' +
              '<aside class="bb__tray" data-bb="tray"></aside>' +
              '<div class="bb__browse-nav" data-bb="nav-browse"></div>' +
            '</div>' +
          '</div>' +
        '</section>' +
        '<section class="bb__panel bb__hidden" data-bb="panel-describe">' +
          '<h2 class="bb__panel-title">Describe your bouquet</h2>' +
          '<div class="bb__describe" data-bb="describe"></div>' +
          '<div class="bb__preview" data-bb="preview"></div>' +
        '</section>' +
        '<div class="bb__nav" data-bb="nav"></div>' +
        '<div class="bb__checkout-bar bb__hidden" data-bb="bar"></div>' +
        '<div class="bb__toast" data-bb="toast" role="status" aria-live="polite"></div>' +
        '<button type="button" class="bb__to-top" data-bb="to-top" aria-label="Back to top">↑</button>';
    }
  }
})();
